'use client'

import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePathname, useRouter } from 'next/navigation'
import Image from 'next/image'
import { ArrowRight, Check, CheckCircle2, CirclePercent, Coins, Copy, Download, ExternalLink, Gift, HandCoins, Info, Loader2, MapPin, Package, Phone, Printer, Receipt, RotateCcw, ShieldCheck, ShoppingBag, Smartphone, Sparkles, Store, Truck, XCircle, Zap } from 'lucide-react'
import QRCodeImport from 'react-qr-code'
import { toast } from '@/lib/toast'
import { navigateUrl } from '@/utils/client-request'
import { FREEFORM_ADDRESS_CHANNELS, STATUS_LABELS, STATUS_TONES, TERMINAL_STATUSES, PAYMENT_METHOD_LABELS, DIGITAL_DOWNLOAD_STATUSES, formatStepDate, formatAddressType, formatOrderAddress, getCustomerName, getVariantDimension, getProductPricing, calcPriceBreakdown, buildDigitalDownloadUrl, downloadDigitalFile, isCustomFieldVisible, bookloDisplayStatus, showsReadyStep, calcDepositBreakdown, getPayableAmount, hasLiveInvoice, resolveOrderStatus, DEPOSIT_LABELS, getOmniwayQrImage } from '@/utils/order'
import DepositBalanceCard from '@/components/blocks/DepositBalanceCard'
import { bankList, formatDateLong, formatDateNormal, formatDateTimeLong, formatImageURL, formatNumber, formatPrice, resolveColorHex } from '@/utils/util'
import { uploadURL } from '@/lib/customFields'
import { usePaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS, type PaymentEndpoint } from '@/lib/commerce/hooks'
import qpayLogo from '@/assets/qpay.svg'
import pocketzeroLogo from '@/assets/pocketzero.svg'
import storepayLogo from '@/assets/storepay.png'
import lendmnLogo from '@/assets/lendmn.png'
import omniwayLogo from '@/assets/omniway.png'

const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>

// productImage stores one bucket object name, or an array of them for multi-select fields.
// Buyer-supplied, so anything uploadURL cannot place in our bucket is dropped.
function customFieldImages(value: any): string[] {
  return (Array.isArray(value) ? value : [value]).map(image => uploadURL(image)).filter((url): url is string => !!url)
}

function renderCustomFieldValue(field: any, dateFormatter: (v: any) => string) {
  if (field.type === 'checkbox') return field.value === 'true' || field.value === true ? 'Тийм' : 'Үгүй'
  if (field.type === 'date') return dateFormatter(field.value)
  if (field.type === 'file') {
    const url = uploadURL(field.value)
    return url && (
      <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-foreground hover:underline">
        Файл харах <ExternalLink className="size-3" />
      </a>
    )
  }
  if (field.type === 'productImage') return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {customFieldImages(field.value).map((image: string) => (
        <a key={image} href={image} target="_blank" rel="noopener noreferrer">
          <img src={image} alt="" className="w-10 h-10 rounded-lg object-cover bg-muted" />
        </a>
      ))}
    </span>
  )
  return field.value
}

type StepKey = 'pending' | 'paid' | 'ready' | 'shipped' | 'delivered' | 'settled'

export default function OrderDetail({ order, vendor }: { order: any, vendor: any }) {
  const pathname = usePathname() || ''
  const router = useRouter()
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0, expired: false })

  useEffect(() => {
    /*
      Split payment: a deposit order stays 'pending' for the whole 7-10 day lead time
      after its deposit lands. settleDeposit nulls expireAt, so the first clause already
      covers it — depositPaidAt is belt-and-braces against a stale cached order, because
      the failure mode is showing a live, paid order as cancelled.
    */
    if (!order?.expireAt || order?.status !== 'pending' || order?.depositPaidAt) return
    const target = new Date(order.expireAt).getTime()
    const tick = () => {
      const diff = Math.max(0, target - Date.now())
      setCountdown({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        expired: diff === 0,
      })
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [order?.expireAt, order?.status, order?.depositPaidAt])

  const isExpired = countdown.expired
  const rawStatus = order?.status as string
  /*
    A split-payment order whose deposit has landed sits in 'pending' for days by design.
    The countdown effect above early-returns for it, but WITHOUT resetting countdown
    state — so an `expired: true` set before the deposit arrived stays latched, and this
    line would then render a live, paid order as cancelled with no way to pay the balance.
  */
  const baseStatus = isExpired && rawStatus === 'pending' && !order?.depositPaidAt ? 'canceled' : rawStatus
  // For Booklo-dispatched orders, surface the Booklo delivery status when it's further
  // along than order.status (the sync cron may lag). Only advances forward.
  const displayStatus = bookloDisplayStatus(order, baseStatus)
  const isTerminal = TERMINAL_STATUSES.has(displayStatus) || displayStatus === 'refunded' || displayStatus === 'partiallyRefunded'
  const isPaidLike = ['paid', 'ready', 'shipped', 'delivered'].includes(displayStatus)
  const isPickup = order?.type === 'pickup'
  const customerName = getCustomerName(order) || ''
  const products: any[] = order?.products || []
  const allDigital = products.length > 0 && products.every((p: any) => (p.product || p)?.isDigital === true)
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const depositInfo = calcDepositBreakdown(order)
  const paymentLabel = PAYMENT_METHOD_LABELS[order?.paymentMethod] || PAYMENT_METHOD_LABELS.transfer

  const addressText = !isPickup && !allDigital ? formatOrderAddress(order?.address, order?.channel) : ''
  const branchText = isPickup && !allDigital && (order?.branchData || order?.branch)
    ? `${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`
    : ''

  const isRefundedStatus = displayStatus === 'refunded' || displayStatus === 'partiallyRefunded'
  // A pay-after-delivery order stays unpaid all the way through fulfilment, so the
  // "pay on handover" reminder has to survive past 'pending'.
  const isUnsettledOnDelivery = order?.payAfterDelivery && !order?.paidAt && rawStatus !== 'canceled'
  // A latched isExpired must not hide the balance-payment block on a deposit-paid order.
  const showPaymentBlock = (!isExpired || !!order?.depositPaidAt) && !isRefundedStatus && (rawStatus === 'pending' || isUnsettledOnDelivery)
  const primary = resolveColorHex(vendor?.color?.primary)

  return (
    <section className="bg-cream sm:p-3 min-h-screen">
      <div className="mx-auto max-w-240">
        <HeroCard
          order={order}
          displayStatus={displayStatus}
          isTerminal={isTerminal}
          isPaidLike={isPaidLike}
          isExpired={isExpired}
          countdown={countdown}
          customerName={customerName}
          totalDue={totalDue}
          paymentLabel={paymentLabel}
        />

        {showPaymentBlock && (
          <div className="mt-4">
            <PendingPaymentBlock order={order} vendor={vendor} onRefresh={() => router.refresh()} primary={primary} />
          </div>
        )}

        {isRefundedStatus && (
          <div className="mt-4">
            <RefundBanner order={order} />
          </div>
        )}

        {isPaidLike && !isTerminal && (
          <div className="mt-4">
            <DigitalDownloadRow order={order} vendor={vendor} primary={primary} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 mt-4">
          <div className="bg-white rounded-3xl p-6 lg:p-7">
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="text-[20px] font-bold font-gilroy tracking-[-0.015em] text-foreground">Хүргэлтийн төлөв</h3>
            </div>

            <Timeline order={order} vendor={vendor} status={displayStatus} isPickup={isPickup} isTerminal={isTerminal} />

            {!allDigital && (
              <div className="mt-5 p-3.5 bg-[#F1EEE7] rounded-2xl flex gap-3 items-start">
                <span className="w-9 h-9 rounded-full bg-white inline-flex items-center justify-center shrink-0">
                  {isPickup ? <Store className="size-4 text-foreground" /> : <MapPin className="size-4 text-foreground" />}
                </span>
                <div className="text-[13px] leading-snug min-w-0">
                  <div className="font-semibold text-foreground">{isPickup ? 'Авах салбар' : 'Хүргэх хаяг'}</div>
                  <div className="text-foreground/60 mt-0.5 wrap-break-word">
                    {isPickup ? (branchText || '—') : (addressText || '—')}
                    {order?.address?.type && !isPickup && !FREEFORM_ADDRESS_CHANNELS.includes(order?.channel) && (
                      <span className="text-foreground/45"> · {formatAddressType(order.address.type)}</span>
                    )}
                  </div>
                  {(order?.phone || order?.additionalPhone) && (
                    <div className="text-foreground/60 mt-1 inline-flex items-center gap-1.5">
                      <Phone className="size-3" /> {order?.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}
                    </div>
                  )}
                </div>
              </div>
            )}

            {(() => {
              const visibleOrderFields = (order?.orderCustomFields || []).filter(isCustomFieldVisible)
              if (visibleOrderFields.length === 0) return null
              return (
                <div className="mt-3 p-3.5 bg-[#F1EEE7] rounded-2xl flex gap-3 items-start">
                  <span className="w-9 h-9 rounded-full bg-white inline-flex items-center justify-center shrink-0">
                    <Info className="size-4 text-foreground" />
                  </span>
                  <div className="text-[13px] leading-snug min-w-0 flex-1">
                    <div className="font-semibold text-foreground">Нэмэлт мэдээлэл</div>
                    <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                      {visibleOrderFields.map((field: any, i: number) => (
                        <div key={i} className="contents">
                          <dt className="text-foreground/55">{field.name}</dt>
                          <dd className="text-foreground/85 min-w-0 wrap-break-word">{renderCustomFieldValue(field, formatDateTimeLong)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              )
            })()}
          </div>

          <div className="bg-white rounded-3xl p-6 lg:p-7">
            <h3 className="text-[20px] font-bold font-gilroy tracking-[-0.015em] text-foreground mb-4 inline-flex items-center gap-2">
              <Package className="size-4.5" /> Захиалгын дэлгэрэнгүй
            </h3>

            <ul className="divide-y divide-foreground/10">
              {products.map((p: any, i: number) => (
                <OrderItem key={`${p.id}-${p.variant?.id}-${i}`} product={p} pathname={pathname} order={order} vendor={vendor} />
              ))}
            </ul>

            <div className="mt-4 pt-4 border-t border-foreground/10 flex flex-col gap-2 text-[13px]">
              <Row label="Барааны үнэ" value={formatPrice(order?.subAmount)} />
              {salePriceSavings > 0 && <Row label="Хямдрал" value={`−${formatPrice(salePriceSavings)}`} tone="green" />}
              {discountBreakdown.map((d: any) => (
                <Row key={d.id} label={`Урамшуулал · ${d.name}`} value={`−${formatPrice(d.amount)}`} tone="green" />
              ))}
              {(order?.couponAmount || 0) > 0 && (
                <Row label={order?.couponData?.name ? `Купон · ${order.couponData.name}` : 'Купон'} value={`−${formatPrice(order?.couponAmount)}`} tone="green" />
              )}
              {(order?.giftCardAmount || 0) > 0 && (
                <Row label="Бэлгийн карт" value={`−${formatPrice(order?.giftCardAmount)}`} tone="green" />
              )}
              {(order?.loyaltyDiscountAmount || 0) > 0 && (
                <Row label="Лоялти хөнгөлөлт" value={`−${formatPrice(order?.loyaltyDiscountAmount)}`} tone="green" />
              )}
              {(order?.loyaltyVoucherAmount || 0) > 0 && (
                <Row label="Лоялти ваучер" value={`−${formatPrice(order?.loyaltyVoucherAmount)}`} tone="green" />
              )}
              {order?.type === 'delivery' && (
                <Row label="Хүргэлт" value={vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? 'Нэмэлтээр тооцно' : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)} tone={order?.deliveryPrice === 0 && vendor?.deliveryType !== 'laterDelivery' ? 'green' : 'default'} />
              )}
              {(order?.totalRefunded || 0) > 0 && (
                <Row label="Буцаалт" value={`−${formatPrice(order?.totalRefunded)}`} tone="red" />
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-dashed border-foreground/15 flex items-baseline justify-between">
              <span className="text-[14px] font-semibold text-foreground">{isPaidLike ? 'Төлсөн' : 'Төлөх дүн'}</span>
              <span className="font-bold font-gilroy tracking-[-0.02em] text-foreground text-[26px]">{formatPrice(totalDue)}</span>
            </div>
            {/* Split payment: how the total divides. Below the grand total, never in
                place of it — this is a split of what is owed, not a change to it. */}
            {depositInfo.isDeposit && (
              <div className="mt-3 pt-3 border-t border-dashed border-foreground/15 flex flex-col gap-2 text-[13.5px]">
                <div className="flex items-baseline justify-between">
                  <span className="text-foreground/60">{DEPOSIT_LABELS.deposit}{depositInfo.isDepositPaid ? ' · төлсөн' : ' · хүлээгдэж буй'}</span>
                  <span className={'tabular-nums ' + (depositInfo.isDepositPaid ? 'text-green-600' : 'text-foreground/80')}>{formatPrice(depositInfo.depositAmount)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className={'flex items-center gap-2 ' + (depositInfo.outstanding > 0 ? 'text-amber-600 font-semibold' : 'text-foreground/60')}>
                    {depositInfo.outstanding > 0 ? DEPOSIT_LABELS.balanceDue : DEPOSIT_LABELS.balance}
                    {depositInfo.isFullyPaid && <span className="text-[11px] text-green-600">Төлсөн</span>}
                  </span>
                  <span className={'tabular-nums font-semibold ' + (depositInfo.isFullyPaid ? 'text-green-600' : depositInfo.outstanding > 0 ? 'text-amber-600' : 'text-foreground/60')}>
                    {formatPrice(depositInfo.balanceAmount) || '0₮'}
                  </span>
                </div>
                {order?.depositTerms && (
                  /* Vendor-authored plain text — rendered as text, never as HTML. */
                  <p className="text-[12px] text-foreground/50 whitespace-pre-line">{order.depositTerms}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 bg-white rounded-3xl p-6 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h3 className="text-[22px] font-bold font-gilroy tracking-[-0.02em] text-foreground">Дахин захиалах</h3>
            <p className="text-[13.5px] text-foreground/60 mt-1">Шинэ бараа, онцгой хямдрал таныг хүлээж байна</p>
          </div>
          <a href={navigateUrl('/product', pathname)} className="inline-flex items-center justify-center gap-2 h-13 px-6 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors self-start sm:self-auto">
            <ShoppingBag className="size-4" /> Бүтээгдэхүүн үзэх <ArrowRight className="size-4" />
          </a>
        </div>

        <div className="my-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12.5px] text-foreground/55 print:hidden">
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors cursor-pointer">
            <Printer className="size-3.5" /> Хэвлэх
          </button>
          <span className="text-foreground/20">·</span>
          <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
            Тусламж
          </a>
          <span className="text-foreground/20">·</span>
          <a href={navigateUrl('/order', pathname)} className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
            Миний захиалгууд <ArrowRight className="size-3.5" />
          </a>
        </div>
      </div>
      <PrintableOrderReceipt order={order} vendor={vendor} displayStatus={displayStatus} totalDue={totalDue} salePriceSavings={salePriceSavings} discountBreakdown={discountBreakdown} customerName={customerName} addressText={addressText} branchText={branchText} isPickup={isPickup} allDigital={allDigital} />
    </section>
  )
}

function PrintableOrderReceipt({ order, vendor, displayStatus, totalDue, salePriceSavings, discountBreakdown, customerName, addressText, branchText, isPickup, allDigital }: { order: any, vendor: any, displayStatus: string, totalDue: number, salePriceSavings: number, discountBreakdown: any[], customerName: string, addressText: string, branchText: string, isPickup: boolean, allDigital: boolean }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  const products: any[] = order?.products || []
  const showAddressOrBranch = !allDigital
  return createPortal(
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{'@page { size: A4; margin: 16mm; }'}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Захиалгын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Захиалга #{order?.customId}</div>
          {order?.createdAt && <div className="text-[11px]">{formatDateTimeLong(order.createdAt)}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{STATUS_LABELS[displayStatus] || '—'}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{customerName || '—'}</div>
          <div>{order?.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}</div>
          {order?.email && <div>{order.email}</div>}
        </div>
        {showAddressOrBranch && (
          <div>
            <div className="text-[10px] uppercase font-bold mb-1">{isPickup ? 'Авах салбар' : 'Хүргэх хаяг'}</div>
            <div>{isPickup ? (branchText || '—') : (addressText || '—')}</div>
          </div>
        )}
      </div>
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Бараа</th>
            <th className="text-right py-1 font-bold w-16">Тоо</th>
            <th className="text-right py-1 font-bold w-24">Үнэ</th>
            <th className="text-right py-1 font-bold w-24">Дүн</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p: any, i: number) => {
            const productData = p.product || p
            const productName = productData?.name || p?.name || 'Бүтээгдэхүүн'
            const { unitPrice } = getProductPricing(p)
            const qty = p?.quantity || 0
            return (
              <tr key={i} className="border-b border-zinc-300 align-top">
                <td className="py-1 pr-2">
                  {productName}
                  {p.variant?.name && <div className="text-[10px]">{p.variant.name}</div>}
                </td>
                <td className="py-1 text-right tabular-nums">{qty}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice)}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice * qty)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between py-0.5"><span>Барааны дүн</span><span className="tabular-nums">{formatPrice(order?.subAmount)}</span></div>
        {salePriceSavings > 0 && <div className="flex justify-between py-0.5"><span>Хямдрал</span><span className="tabular-nums">-{formatPrice(salePriceSavings)}</span></div>}
        {discountBreakdown.map((d: any) => (
          <div key={d.id} className="flex justify-between py-0.5"><span>Урамшуулал ({d.name})</span><span className="tabular-nums">-{formatPrice(d.amount)}</span></div>
        ))}
        {(order?.couponAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Купон{order?.couponData?.name ? ` (${order.couponData.name})` : ''}</span><span className="tabular-nums">-{formatPrice(order?.couponAmount)}</span></div>
        )}
        {(order?.giftCardAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Бэлгийн карт</span><span className="tabular-nums">-{formatPrice(order?.giftCardAmount)}</span></div>
        )}
        {(order?.loyaltyDiscountAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лоялти хөнгөлөлт</span><span className="tabular-nums">-{formatPrice(order?.loyaltyDiscountAmount)}</span></div>
        )}
        {(order?.loyaltyVoucherAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лоялти ваучер</span><span className="tabular-nums">-{formatPrice(order?.loyaltyVoucherAmount)}</span></div>
        )}
        {order?.type === 'delivery' && (
          <div className="flex justify-between py-0.5"><span>Хүргэлт</span><span className="tabular-nums">{vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? 'Нэмэлтээр тооцно' : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)}</span></div>
        )}
        {(order?.totalRefunded || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Буцаалт</span><span className="tabular-nums">-{formatPrice(order?.totalRefunded)}</span></div>
        )}
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт төлөх</span>
          <span className="tabular-nums">{formatPrice(totalDue)}</span>
        </div>
      </div>
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>,
    document.body,
  )
}

function HeroCard({ order, displayStatus, isTerminal, isPaidLike, isExpired, countdown, customerName, totalDue, paymentLabel }: {
  order: any
  displayStatus: string
  isTerminal: boolean
  isPaidLike: boolean
  isExpired: boolean
  countdown: { h: number, m: number, s: number, expired: boolean }
  customerName: string
  totalDue: number
  paymentLabel: string
}) {
  const pathname = usePathname() || ''
  // 'pending' means waiting for money — not true of an order with nothing owed up front.
  // See resolveOrderStatus.
  const shownStatus = resolveOrderStatus(order, displayStatus)
  const tone = STATUS_TONES[shownStatus] || STATUS_TONES.pending
  const label = STATUS_LABELS[shownStatus] || 'Тодорхойгүй'

  let icon: React.ReactNode = <Check className="size-9" strokeWidth={2.5} />
  let heading = 'Захиалга баталгаажлаа.'
  let lede: React.ReactNode = null

  if (displayStatus === 'delivered') {
    heading = 'Захиалга хүргэгдлээ.'
    lede = <>Таны захиалга амжилттай хүргэгдсэн байна.</>
  } else if (displayStatus === 'shipped') {
    icon = <Truck className="size-9" strokeWidth={2} />
    heading = 'Захиалга хүргэлтэнд гарлаа.'
    lede = <>Таны захиалга хүргэлтэнд гарсан байна.</>
  } else if (isPaidLike) {
    heading = 'Захиалга баталгаажлаа.'
    lede = (
      <>
        {paymentLabel} төлбөрийн аргаар <b className="text-foreground tabular-nums">{formatPrice(totalDue)}</b> амжилттай төлөгдлөө.
        {order?.email && <> {order.email} имэйл хаягт захиалгын мэдээлэл илгээгдлээ.</>}
      </>
    )
  } else if (displayStatus === 'pending' && !isExpired) {
    icon = <Receipt className="size-9" strokeWidth={2} />
    heading = 'Төлбөрөө төлнө үү.'
    lede = (
      <>
        {paymentLabel} төлбөрийн аргаар <b className="text-foreground tabular-nums">{formatPrice(totalDue)}</b> төлбөр хүлээгдэж байна.
      </>
    )
  } else if (isTerminal) {
    const isRefund = displayStatus === 'refunded' || displayStatus === 'partiallyRefunded'
    icon = isRefund ? <RotateCcw className="size-9" strokeWidth={2} /> : <XCircle className="size-9" strokeWidth={2} />
    heading = label + '.'
    const reason = displayStatus === 'canceled' ? order?.canceledReason
      : isRefund ? order?.refundedReason
        : displayStatus === 'deliveryFailed' ? order?.deliveryFailedReason : null
    lede = reason
      ? <>{reason}</>
      : isRefund
        ? <>Таны төлбөр 3-5 ажлын өдөрт буцаагдах болно.</>
        : <>Захиалга амжилтгүй</>
  }

  const greetingName = customerName ? `${customerName.split(' ')[0]}.` : ''

  return (
    <div className="bg-white rounded-3xl p-7 sm:p-10 relative overflow-hidden">
      <div className="absolute -top-16 -right-12 w-60 h-60 rounded-full opacity-70 pointer-events-none" style={{ background: tone.bg }} />
      <div className="absolute -top-4 -right-2 w-16 h-16 rounded-full opacity-80 pointer-events-none" style={{ background: tone.ring }} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-5">
          <div className="w-18 h-18 rounded-full inline-flex items-center justify-center" style={{ background: tone.bg, color: tone.fg }}>
            {icon}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-foreground/55">
            <span>Захиалга</span>
            <button
              onClick={() => {
                if (!order?.customId) return
                navigator.clipboard?.writeText(order.customId)
                toast.success('Захиалгын дугаар хуулагдлаа')
              }}
              className="inline-flex items-center gap-1.5 text-foreground hover:opacity-70 transition-opacity cursor-pointer tabular-nums"
            >
              #{order?.customId} <Copy className="size-3" />
            </button>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold normal-case tracking-normal" style={{ background: tone.bg, color: tone.fg }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} /> {label}
            </span>
          </div>
        </div>

        <h1 className="font-bold font-gilroy tracking-[-0.03em] leading-[0.98] text-foreground mt-2" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}>
          {greetingName && <>Баярлалаа, {greetingName}<br /></>}{heading}
        </h1>

        {lede && (
          <p className="mt-4 text-[15px] text-foreground/65 max-w-140 leading-relaxed">{lede}</p>
        )}
        {displayStatus === 'pending' && !isExpired && (
          <CountdownBlock countdown={countdown} />
        )}
        {(order?.loyaltyPointsAwarded || 0) > 0 && (
          <a href={navigateUrl('/points', pathname)} className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-[#F1EEE7] px-4 py-3 hover:bg-[#E9E4DA] transition-colors">
            <span className="w-9 h-9 rounded-full bg-white inline-flex items-center justify-center shrink-0">
              <Coins className="size-4 text-foreground" />
            </span>
            <span className="text-[13.5px] text-foreground/60">
              <span className="font-bold text-foreground tabular-nums">+{formatNumber(order.loyaltyPointsAwarded)} оноо</span> энэ захиалгаас цуглууллаа
            </span>
            <ArrowRight className="size-4 text-foreground shrink-0" />
          </a>
        )}
      </div>
    </div>
  )
}

function CountdownBlock({ countdown }: { countdown: { h: number, m: number, s: number, expired: boolean } }) {
  const pad = (n: number) => String(n).padStart(2, '0')
  const units = [
    { v: countdown.h, lab: 'цаг' },
    { v: countdown.m, lab: 'мин' },
    { v: countdown.s, lab: 'сек' },
  ]
  return (
    <div className="mt-5 inline-flex items-center gap-2 p-1.5 rounded-2xl bg-[#F1EEE7]">
      {units.map((u, i) => (
        <div key={i} className="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-white">
          <span className="text-[18px] font-bold font-gilroy tracking-[-0.02em] text-foreground leading-none tabular-nums">{pad(u.v)}</span>
          <span className="text-[10px] text-foreground/55 mt-1 uppercase tracking-wider">{u.lab}</span>
        </div>
      ))}
    </div>
  )
}

function Timeline({ order, vendor, status, isPickup, isTerminal }: { order: any, vendor: any, status: string, isPickup: boolean, isTerminal: boolean }) {
  if (isTerminal) {
    const tone = STATUS_TONES[status] || STATUS_TONES.refunded
    const isRefund = status === 'refunded' || status === 'partiallyRefunded'
    const stamp = status === 'canceled' ? order?.canceledAt
      : isRefund ? order?.refundedAt
        : status === 'deliveryFailed' ? order?.deliveryFailedAt : null
    return (
      <div className="flex items-center gap-3 px-4 py-4 rounded-2xl" style={{ background: tone.bg }}>
        <span className="w-9 h-9 rounded-full bg-white inline-flex items-center justify-center" style={{ color: tone.fg }}>
          {isRefund ? <RotateCcw className="size-4" /> : <XCircle className="size-4" />}
        </span>
        <div className="flex-1">
          <div className="text-[14px] font-semibold" style={{ color: tone.fg }}>{STATUS_LABELS[status]}</div>
          <div className="text-[12px] mt-0.5" style={{ color: tone.fg, opacity: 0.75 }}>
            {stamp ? formatDateTimeLong(stamp) : 'Захиалгын хугацаа дууссан'}
          </div>
        </div>
      </div>
    )
  }

  // Pay-after-delivery reverses the last two steps: the goods arrive, then the
  // money. Payment gets its own trailing step keyed off paidAt, since order.status
  // never passes through 'paid' for these.
  const isOnDelivery = order?.payAfterDelivery
  const baseSteps: Array<{ key: StepKey, label: string, at: any }> = isOnDelivery ? [
    { key: 'pending', label: 'Захиалга хийгдсэн', at: order?.createdAt },
    { key: 'ready', label: 'Бэлэн болсон', at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд гарсан', at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', at: order?.deliveredAt },
    { key: 'settled', label: 'Төлбөр баталгаажсан', at: order?.paidAt },
  ] : [
    { key: 'pending', label: 'Захиалга хийгдсэн', at: order?.createdAt },
    { key: 'paid', label: 'Төлбөр баталгаажсан', at: order?.paidAt },
    { key: 'ready', label: 'Бэлэн болсон', at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд гарсан', at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', at: order?.deliveredAt },
  ]
  const hasReadyStep = showsReadyStep(order, vendor)
  const steps = baseSteps.filter(s => !(isPickup && s.key === 'shipped') && !(!hasReadyStep && s.key === 'ready'))
  // `status` here is the Booklo-aware displayStatus, so the stepper advances correctly.
  const targetKey = status === 'partiallyRefunded' ? 'delivered' : status
  let curIdx = Math.max(0, steps.findIndex(s => s.key === targetKey))
  /*
    Payment sits last in this layout because a pay-after-delivery order settles at the
    door — so paidAt implied everything before it was done. A split payment breaks that:
    the customer can clear the balance days before the goods move, and jumping the marker
    to the end painted "Хүргэлтэнд" and "Хүргэгдсэн" as complete when neither had
    happened. Only advance when the goods actually went out; the settled step still ticks
    on its own via the `at` rule below.
  */
  if (isOnDelivery && order?.paidAt && (status === 'delivered' || order?.deliveredAt)) curIdx = steps.length - 1

  return (
    <div className="relative">
      {steps.map((s, i) => {
        // A step past the marker that carries its own timestamp really did happen —
        // paying the balance early ticks the settled step without implying delivery.
        const done = i < curIdx || (i > curIdx && !!s.at)
        const active = i === curIdx
        const isLast = i === steps.length - 1
        return (
          <div key={s.key} className="grid grid-cols-[24px_1fr] gap-3 relative pb-5 last:pb-0">
            {!isLast && (
              <div className="absolute left-2.75 top-5.5 -bottom-1 w-0.5 bg-foreground/10">
                {done && <div className="absolute inset-0 bg-emerald-500" />}
              </div>
            )}
            <div className={`w-5.5 h-5.5 rounded-full inline-flex items-center justify-center relative z-10 ${done ? 'bg-emerald-500 text-white' : active ? 'bg-white border-[2.5px] border-foreground' : 'bg-white border-2 border-foreground/20'}`} style={active ? { boxShadow: '0 0 0 6px rgba(20,20,30,0.08)' } : undefined}>
              {done && <Check className="size-3" strokeWidth={3} />}
              {active && <span className="w-2 h-2 rounded-full bg-foreground" />}
            </div>
            <div>
              <div className={`text-[14px] ${done || active ? 'font-semibold text-foreground' : 'font-medium text-foreground/50'}`}>{s.label}</div>
              <div className="text-[12px] text-foreground/55 mt-0.5">
                {s.at ? formatStepDate(s.at) : '—'}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function OrderItem({ product, pathname, order, vendor }: { product: any, pathname: string, order: any, vendor: any }) {
  const [downloading, setDownloading] = useState(false)
  const productData = product.product || product
  const productName = productData?.name || product?.name || 'Бүтээгдэхүүн'
  const productCustomId = productData?.customId || product?.customId
  const variant = product.variant
  const isReward = product.isRewardItem
  const discount = !isReward ? product.discount : null
  const hasDiscount = discount && (discount.type === 'productDiscount' || discount.type === 'order')
  const { unitPrice, originalUnitPrice, discountApplied } = getProductPricing(product)
  const dimension = getVariantDimension(variant)
  const isDigital = productData?.isDigital
  const variantImage = variant?.images?.[0] || 'placeholder.png'
  const qty = (product?.quantity || 0) + (product?.refundedQuantity || 0)
  const hasDiscountedPrice = originalUnitPrice !== null && unitPrice < originalUnitPrice
  const visibleCustomFields = (product.customFields || []).filter(isCustomFieldVisible)
  const isFullyRefunded = product?.refunded && product?.quantity === 0
  const isPartiallyRefunded = product?.refunded && product?.quantity > 0

  const downloadStatus = isDigital && variant?.id
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === variant.id) || null
    : null
  const isExpired = !!downloadStatus?.expired
  const isLimitHit = !!downloadStatus && downloadStatus.downloadsRemaining === 0 && !isExpired
  const downloadAllowed = isDigital && variant?.digitalFile && DIGITAL_DOWNLOAD_STATUSES.has(order?.status) && !isExpired && !isLimitHit
  const downloadUrl = downloadAllowed ? buildDigitalDownloadUrl(order, vendor, variant.id) : null
  const showBadgeRow = isReward || (hasDiscount && discountApplied) || isDigital || isFullyRefunded || isPartiallyRefunded

  return (
    <li className={`flex gap-3.5 py-3.5 first:pt-0 last:pb-0 ${isFullyRefunded ? 'opacity-70' : ''}`}>
      <a href={navigateUrl(`/product/${productCustomId}`, pathname)} className="shrink-0">
        <img src={formatImageURL(variantImage)} alt={productName} className={`w-14 h-14 rounded-xl object-cover bg-[#F1EEE7] ${isFullyRefunded ? 'grayscale' : ''}`} />
      </a>
      <div className="flex-1 min-w-0">
        <a href={navigateUrl(`/product/${productCustomId}`, pathname)} className={`block text-[13.5px] font-semibold line-clamp-1 hover:underline ${isFullyRefunded ? 'text-foreground/50 line-through' : 'text-foreground'}`}>{productName}</a>
        <div className="text-[12px] text-foreground/55 mt-0.5">
          {variant?.name || ''}{dimension ? `${variant?.name ? ' · ' : ''}${dimension}` : ''}{qty > 0 ? `${(variant?.name || dimension) ? ' · ' : ''}× ${qty}` : ''}
        </div>
        {showBadgeRow && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {isFullyRefunded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-semibold">
                <RotateCcw className="size-3" strokeWidth={2.5} /> Буцаагдсан
              </span>
            )}
            {isPartiallyRefunded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold">
                <RotateCcw className="size-3" strokeWidth={2.5} /> Хэсэгчлэн · {product?.refundedQuantity}ш
              </span>
            )}
            {isReward && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                <Gift className="size-3" strokeWidth={2.5} /> {product.discountName || 'Бэлэг'}
              </span>
            )}
            {hasDiscount && discountApplied && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[11px] font-semibold">
                <CirclePercent className="size-3" strokeWidth={2.5} /> {discount.name}
              </span>
            )}
            {isDigital && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold">Дижитал</span>
            )}
          </div>
        )}
        {isDigital && variant?.digitalFile && DIGITAL_DOWNLOAD_STATUSES.has(order?.status) && (
          <div className="mt-2 flex flex-col gap-1.5">
            {downloadUrl ? (
              <button onClick={async () => { setDownloading(true); await downloadDigitalFile(downloadUrl); setDownloading(false) }} disabled={downloading} className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold hover:bg-foreground/90 transition-colors cursor-pointer w-fit disabled:opacity-60 disabled:pointer-events-none">
                {downloading ? <Loader2 className="size-3 animate-spin" /> : <Download className="size-3" />} {downloading ? 'Татаж байна...' : 'Файл татах'}
              </button>
            ) : (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground/5 text-foreground/55 text-[11.5px] font-medium w-fit">
                <Download className="size-3" /> {isExpired ? 'Татах хугацаа дууссан' : 'Татах хязгаар хэтэрсэн'}
              </div>
            )}
            {downloadStatus && !isExpired && (
              <div className="text-[11px] text-foreground/55 tabular-nums">
                Үлдсэн: <span className="font-semibold text-foreground">{downloadStatus.downloadsRemaining}/{downloadStatus.downloadsMax}</span>
                {downloadStatus.expiresAt && <span> · {formatDateNormal(new Date(downloadStatus.expiresAt))} хүртэл</span>}
              </div>
            )}
          </div>
        )}
        {visibleCustomFields.length > 0 && (
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
            {visibleCustomFields.map((field: any, i: number) => (
              <div key={i} className="contents">
                <dt className="text-foreground/50">{field.name}</dt>
                <dd className="text-foreground/85 min-w-0 wrap-break-word">{renderCustomFieldValue(field, formatDateLong)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      <div className="text-right shrink-0">
        {isReward ? (
          <>
            <div className="text-[13.5px] font-semibold text-emerald-600 tabular-nums">{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
            {originalUnitPrice !== null && <div className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(originalUnitPrice)}</div>}
          </>
        ) : hasDiscountedPrice ? (
          <>
            <div className={`text-[13.5px] font-semibold tabular-nums ${unitPrice === 0 ? 'text-emerald-600' : 'text-red-600'}`}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
            <div className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(originalUnitPrice)}</div>
          </>
        ) : (
          <div className="text-[13.5px] font-semibold text-foreground tabular-nums">{formatPrice(unitPrice || variant?.price || 0)}</div>
        )}
      </div>
    </li>
  )
}

function Row({ label, value, tone = 'default' }: { label: string, value: any, tone?: 'default' | 'green' | 'red' }) {
  const colorClass = tone === 'green' ? 'text-emerald-600' : tone === 'red' ? 'text-red-600' : 'text-foreground'
  return (
    <div className="flex items-center justify-between">
      <span className="text-foreground/60">{label}</span>
      <span className={`font-medium tabular-nums ${colorClass}`}>{value}</span>
    </div>
  )
}

function RefundBanner({ order }: { order: any }) {
  const status = order?.status
  if (status !== 'refunded' && status !== 'partiallyRefunded') return null
  const isPartial = status === 'partiallyRefunded'
  const refundedAmount = order?.totalRefunded || (isPartial ? 0 : order?.amount)
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7 flex items-start gap-3">
      <span className="w-10 h-10 rounded-full bg-[#F1EEE7] inline-flex items-center justify-center shrink-0">
        <RotateCcw className="size-4 text-foreground/70" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-foreground">
          {isPartial ? 'Хэсэгчилсэн буцаалт' : 'Бүрэн буцаалт'}
          {order?.refundedReason ? <span className="ml-2 text-[12px] font-normal text-foreground/55">({order.refundedReason})</span> : null}
        </div>
        <div className="text-[12.5px] text-foreground/60 mt-1 leading-snug">
          Таны <span className="font-semibold text-foreground tabular-nums">{formatPrice(refundedAmount)}</span> нь 3-5 ажлын өдөрт буцаагдах болно. Хэрэв хүлээн аваагүй тохиолдолд дэлгүүртэй холбогдоно уу.
        </div>
      </div>
    </div>
  )
}

function DigitalDownloadRow({ order, vendor, primary }: { order: any, vendor: any, primary: string }) {
  const [downloading, setDownloading] = useState(false)
  const digitalProducts = (order?.products || []).filter((p: any) => {
    const productData = p.product || p
    return productData?.isDigital && p.variant?.digitalFile
  })
  if (!digitalProducts.length || !DIGITAL_DOWNLOAD_STATUSES.has(order?.status)) return null
  const single = digitalProducts.length === 1
  const firstStatus = single
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === digitalProducts[0].variant.id) || null
    : null
  const firstUnavailable = !!firstStatus && (firstStatus.expired || firstStatus.downloadsRemaining === 0)
  const firstUrl = single && !firstUnavailable ? buildDigitalDownloadUrl(order, vendor, digitalProducts[0].variant.id) : null
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
        <span className="w-10 h-10 rounded-full bg-blue-50 inline-flex items-center justify-center shrink-0">
          <Download className="size-4 text-blue-700" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-foreground">Дижитал бүтээгдэхүүн татах</div>
          <div className="text-[12.5px] text-foreground/60 mt-0.5">
            {single ? 'Доорх товчлуурыг дарж файлаа татаж авна уу.' : `${digitalProducts.length} файл бэлэн боллоо. Жагсаалт дотроос тус бүрд нь татна уу.`}
          </div>
        </div>
      </div>
      {single && firstUrl && (
        <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
          <button onClick={async () => { setDownloading(true); await downloadDigitalFile(firstUrl); setDownloading(false) }} disabled={downloading} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors cursor-pointer disabled:opacity-60 disabled:pointer-events-none">
            {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} {downloading ? 'Татаж байна...' : 'Файл татах'}
          </button>
          {firstStatus && !firstStatus.expired && (
            <div className="text-[11px] text-foreground/55 tabular-nums">
              Үлдсэн: <span className="font-semibold text-foreground">{firstStatus.downloadsRemaining}/{firstStatus.downloadsMax}</span>
              {firstStatus.expiresAt && <span> · {formatDateNormal(new Date(firstStatus.expiresAt))} хүртэл</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function OnDeliveryCard({ order }: { order: any }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-700 grid place-items-center shrink-0">
        <HandCoins className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">Барааг авахдаа төлөх</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Захиалгын дүн <strong>{formatPrice(order?.amount)}</strong>-г барааг хүлээн авахдаа төлнө үү. Урьдчилж төлөх шаардлагагүй.
        </div>
      </div>
    </div>
  )
}

function PendingPaymentBlock({ order, vendor, onRefresh, primary }: { order: any, vendor: any, onRefresh: () => void, primary: string }) {
  /*
    Split payment is checked first, and the order matters: a deposit order flips
    payAfterDelivery on the moment the deposit lands, so the onDelivery branch would
    otherwise swallow the balance state and tell a customer who owes money to "pay at the
    door", with no way to pay online. While the deposit itself is due the flag is still
    false, so it falls through to the rail blocks, which render getPayableAmount.
  */
  const deposit = calcDepositBreakdown(order)
  if (deposit.isAwaitingBalance && !hasLiveInvoice(order)) {
    return <DepositBalanceCard order={order} vendor={vendor} onRefresh={onRefresh} />
  }
  if (order?.payAfterDelivery && !deposit.isDeposit) return <OnDeliveryCard order={order} />
  if (order?.paymentMethod === 'qpay') return <QpayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'storepay') return <StorepayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'pocket') return <PocketBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'lend') return <LendBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  // A cancelled OmniWay invoice is not payable — fall through to the fallback cards below.
  if (order?.paymentMethod === 'omniway' && !order?.omniway?.canceledAt) return <OmniwayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  return <BankTransferCard order={order} primary={primary} />
}

function PaymentSuccessCard() {
  return (
    <div className="bg-white rounded-3xl p-6 flex items-center gap-3">
      <span className="w-11 h-11 rounded-full bg-emerald-50 inline-flex items-center justify-center shrink-0">
        <CheckCircle2 className="size-5 text-emerald-600" />
      </span>
      <div className="flex-1">
        <div className="text-[14px] font-semibold text-foreground">Төлбөр амжилттай төлөгдлөө</div>
        <div className="text-[12.5px] text-foreground/60 mt-0.5">Захиалга баталгаажиж, бид удахгүй боловсруулж эхэлнэ.</div>
      </div>
    </div>
  )
}

function CheckPaymentButton({ loading, success, onClick }: { loading: boolean, success: boolean, onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={loading} className="w-full h-12 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
      {loading ? 'Шалгаж байна...' : success ? 'Төлбөр төлөгдсөн байна' : 'Төлбөр шалгах'}
    </button>
  )
}

function MethodHeader({ logo, title, subtitle, badge }: { logo: React.ReactNode, title: string, subtitle?: string, badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pb-4 border-b border-foreground/10">
      <div className="w-11 h-11 rounded-xl bg-[#F1EEE7] inline-flex items-center justify-center shrink-0 p-2 overflow-hidden">{logo}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold">Төлбөрийн арга</div>
        <div className="text-[15px] font-semibold text-foreground truncate">{title}{subtitle ? <span className="text-foreground/55 font-normal"> · {subtitle}</span> : null}</div>
      </div>
      {badge}
    </div>
  )
}

// `image`: value is a ready-made QR image (data URI) from the provider, not a string to encode.
function QRFrame({ value, accent, logoSrc, size = 200, image }: { value: string, accent: string, logoSrc?: any, size?: number, image?: boolean }) {
  if (!value) {
    return <div className="rounded-2xl bg-foreground/5 animate-pulse" style={{ width: size + 32, height: size + 32 }} />
  }
  const corners = [
    { pos: { top: 0, left: 0 }, border: 'border-t-2 border-l-2 rounded-tl-2xl' },
    { pos: { top: 0, right: 0 }, border: 'border-t-2 border-r-2 rounded-tr-2xl' },
    { pos: { bottom: 0, left: 0 }, border: 'border-b-2 border-l-2 rounded-bl-2xl' },
    { pos: { bottom: 0, right: 0 }, border: 'border-b-2 border-r-2 rounded-br-2xl' },
  ]
  return (
    <div className="relative inline-block" style={{ width: size + 32, height: size + 32 }}>
      <div className="absolute inset-0 pointer-events-none">
        {corners.map((c, i) => (
          <div key={i} className={`absolute w-5 h-5 ${c.border}`} style={{ ...c.pos, borderColor: accent }} />
        ))}
      </div>
      <div className="absolute inset-2 rounded-xl bg-white p-3">
        {image
          ? <img src={value} alt="QR" className="w-full h-full object-contain" />
          : <QRCode value={value} size={size - 8} fgColor={accent} bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />}
        {logoSrc && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-md bg-white border border-foreground/10 grid place-items-center p-1">
            <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

function NumberedSteps({ steps, accent }: { steps: string[], accent: string }) {
  return (
    <ol className="space-y-2 text-[13px] text-foreground">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-white mt-0.5" style={{ background: accent }}>{i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function CopyField({ label, value, mono }: { label: string, value: string, mono?: boolean }) {
  const onCopy = () => {
    if (!value) return
    navigator.clipboard?.writeText(value)
    toast.success(`${label} хуулагдлаа`)
  }
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1.5">{label}</div>
      <button onClick={onCopy} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-[#F1EEE7] hover:bg-[#E8E3D8] transition-colors cursor-pointer">
        <span className={`text-[13.5px] font-semibold text-foreground truncate ${mono ? 'font-mono tabular-nums' : ''}`}>{value || '—'}</span>
        <Copy className="size-3.5 text-foreground/55 shrink-0" />
      </button>
    </div>
  )
}

function BankTransferCard({ order, primary }: { order: any, primary: string }) {
  const bank = order?.vendor?.bank
  const bankName = bank?.bankName || '—'
  const bankIcon = bankList.find(b => b.code == bank?.bankCode)?.icon
  const reference = `#${order?.customId}`
  return (
    <div className="bg-white rounded-3xl overflow-hidden">
      <div className="p-6 lg:p-7">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl overflow-hidden border border-foreground/10 bg-white grid place-items-center shrink-0">
            {bankIcon && <Image src={bankIcon} width={44} height={44} alt={bankName} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold">Хүлээн авагч банк</div>
            <div className="text-[15px] font-semibold text-foreground truncate">{bankName}</div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-emerald-600 font-semibold">
            <ShieldCheck className="size-4" /> Баталгаажсан
          </div>
        </div>
        <div className="mt-5 grid gap-3">
          <CopyField label="IBAN · Дансны дугаар" value={bank?.accountIban || ''} mono />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1.5">Хүлээн авагч</div>
              <div className="px-3 py-2.5 rounded-xl bg-[#F1EEE7] text-[13.5px] font-semibold text-foreground">{bank?.accountName || '—'}</div>
            </div>
            <CopyField label="Гүйлгээний утга" value={reference} mono />
          </div>
        </div>
      </div>
      <div className="px-6 lg:px-7 py-5" style={{ background: primary, color: '#ffffff' }}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Шилжүүлэх дүн</div>
            <div className="text-3xl font-bold font-gilroy tracking-[-0.02em] tabular-nums">{formatPrice(getPayableAmount(order))}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Захиалга</div>
            <div className="text-[15px] font-mono font-semibold">#{order?.customId}</div>
          </div>
        </div>
      </div>
      <div className="px-6 lg:px-7 py-4 flex items-start gap-2 text-[12px] text-foreground/60 bg-[#F1EEE7]">
        <Info className="size-3.5 shrink-0 mt-0.5" />
        <p>Гүйлгээний утга хэсэгт <b className="font-semibold text-foreground">#{order?.customId}</b> захиалгын дугаарыг заавал бичнэ үү. Төлбөр хүлээн авмагц захиалга баталгаажна.</p>
      </div>
    </div>
  )
}

function QpayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'qpay', onRefresh })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ order })
  if (success) return <PaymentSuccessCard />
  if (!order) return null
  const banks = order?.qpay?.urls || []
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7">
      <MethodHeader
        logo={<Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />}
        title="QPay"
        subtitle="QR төлбөр"
        badge={<div className="hidden sm:flex items-center gap-1.5 text-[12px] text-emerald-600 font-semibold"><Zap className="size-3.5" /> Шуурхай</div>}
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 pt-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.qpay?.QRCode} accent="#0f172a" logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] text-foreground/55 font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1">Төлөх дүн</div>
          <div className="text-3xl font-bold font-gilroy tracking-[-0.02em] tabular-nums text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-4">
            <NumberedSteps accent="#0f172a" steps={['Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу', 'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу']} />
          </div>
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
      {banks.length > 0 && (
        <div className="mt-5 pt-5 border-t border-foreground/10 sm:hidden">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-2.5">Банкны апп шууд нээх</div>
          {isInAppBrowser && (
            <div className="rounded-2xl bg-[#F1EEE7] px-4 py-3.5 mb-3">
              <p className="text-[12.5px] text-foreground/75">{IN_APP_BROWSER_LABELS.message}</p>
              <a href={browserUrl} onClick={openInBrowser} className="mt-3 inline-flex items-center gap-2 justify-center h-10 px-4 rounded-full bg-foreground text-cream text-[13px] font-semibold">
                {IN_APP_BROWSER_LABELS.action} <ExternalLink className="size-3.5" />
              </a>
              <p className="mt-2.5 text-[11.5px] text-foreground/55">{IN_APP_BROWSER_LABELS.hint}</p>
            </div>
          )}
          {!appLinksBlocked && (
            <div className="grid grid-cols-6 gap-2">
              {banks.map((b: any, i: number) => (
                <a key={i} href={b.link} className="aspect-square rounded-xl bg-[#F1EEE7] overflow-hidden hover:opacity-80 transition-opacity">
                  <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PocketBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'pocket', onRefresh })
  const accent = '#e42f56'
  if (success) return <PaymentSuccessCard />
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7">
      <MethodHeader
        logo={<Image src={pocketzeroLogo} alt="PocketZero" className="w-full h-full object-contain" />}
        title="PocketZero"
        subtitle="Хүүгүй зээл"
        badge={<span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#fef2f2', color: accent }}>0% ХҮҮТЭЙ</span>}
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 pt-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.pocket?.qr} accent={accent} logoSrc={pocketzeroLogo} size={200} />
          <div className="mt-2 text-[11px] text-foreground/55 font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold font-gilroy tracking-[-0.02em] tabular-nums text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-2 text-[12px] text-foreground/60">Сард тэнцүү хуваан төлөх боломжтой. Нэмэлт хүү, шимтгэлгүй.</div>
          <div className="mt-4">
            <NumberedSteps accent={accent} steps={['Pocket аппыг нээж PocketZero цэс рүү орно уу', 'Дээрх QR кодыг уншуулж нөхцөлөө баталгаажуулна уу']} />
          </div>
          {order?.pocket?.deeplink && (
            <a href={order.pocket.deeplink} className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center h-10 px-4 rounded-full text-white text-[13px] font-semibold self-start" style={{ background: accent }}>
              <Image src={pocketzeroLogo} alt="" className="w-4 h-4 object-contain" /> Pocket App нээх <ExternalLink className="size-3.5" />
            </a>
          )}
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
    </div>
  )
}

function OmniwayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'omniway', onRefresh })
  const accent = '#ffc800'
  if (success) return <PaymentSuccessCard />
  // A cancelled invoice can no longer be paid — never show its stale QR.
  if (order?.omniway?.canceledAt) return null
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7">
      <MethodHeader
        logo={<Image src={omniwayLogo} alt="OmniWay" className="w-full h-full object-contain" />}
        title="OmniWay"
        subtitle="Хуваан төлөх"
      />
      <div className="grid sm:grid-cols-[auto_1fr] gap-5 pt-5">
        <div className="flex flex-col items-center">
          <QRFrame value={getOmniwayQrImage(order)} accent={accent} size={200} image />
          <div className="mt-2 text-[11px] text-foreground/55 font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold font-gilroy tracking-[-0.02em] tabular-nums text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-3 text-[13px] text-foreground">OmniWay аппликэйшнээр QR кодыг уншуулж төлнө үү</div>
          {order?.omniway?.qrContent && (
            <a href={order.omniway.qrContent} className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center h-10 px-4 rounded-full text-zinc-900 text-[13px] font-semibold self-start" style={{ background: accent }}>
              <Image src={omniwayLogo} alt="" className="w-4 h-4 object-contain rounded-sm" /> OmniWay-ээр төлөх <ExternalLink className="size-3.5" />
            </a>
          )}
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
      </div>
    </div>
  )
}

function PhoneMethodCard({ order, vendor, method, config, onRefresh }: { order: any, vendor: any, method: PaymentEndpoint, config: { brand: string, tagline: string, appName: string, accent: string, logo: any, phone: string, steps: string[] }, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method, onRefresh })
  if (success) return <PaymentSuccessCard />
  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7">
      <MethodHeader logo={config.logo} title={config.brand} subtitle={config.tagline} />
      <div className="grid sm:grid-cols-2 gap-5 pt-5">
        <div className="flex flex-col">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold font-gilroy tracking-[-0.02em] tabular-nums text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-5 text-[10.5px] uppercase tracking-[0.12em] text-foreground/55 font-semibold mb-1.5">Бүртгэлтэй утасны дугаар</div>
          <div className="relative">
            <Phone className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55" />
            <div className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#F1EEE7] text-[13.5px] font-mono tabular-nums font-semibold text-foreground">{config.phone || '—'}</div>
          </div>
          <div className="mt-1.5 text-[11px] text-foreground/55">{config.appName} апп дээр бүртгэлтэй утасны дугаар.</div>
          <div className="mt-5"><CheckPaymentButton loading={loading} success={success} onClick={check} /></div>
        </div>
        <div className="relative rounded-2xl p-5" style={{ background: `linear-gradient(145deg, ${config.accent}1f, ${config.accent}08)` }}>
          <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold mb-3" style={{ color: config.accent }}>Хэрхэн төлөх</div>
          <NumberedSteps accent={config.accent} steps={config.steps} />
          <div className="mt-4 flex items-center gap-2 text-[11px] text-foreground/55">
            <Smartphone className="size-3.5" style={{ color: config.accent }} />
            Мэдэгдэл таны утсанд хэдхэн секундийн дотор очно.
          </div>
        </div>
      </div>
    </div>
  )
}

function StorepayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="storepay"
      onRefresh={onRefresh}
      config={{
        brand: 'StorePay',
        tagline: 'Хуваан төлөх үйлчилгээ',
        appName: 'StorePay',
        accent: '#0ea5e9',
        phone: order?.storepay?.phone,
        logo: <Image src={storepayLogo} alt="StorePay" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'StorePay апп дээрх ирсэн нэхэмжлэлийн Төлөх товчлуурыг дарна',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

function LendBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="lend"
      onRefresh={onRefresh}
      config={{
        brand: 'LendMN',
        tagline: 'Бэлэн мөнгөн зээл',
        appName: 'LendMN',
        accent: '#f97316',
        phone: order?.lend?.phoneNumber,
        logo: <Image src={lendmnLogo} alt="LendMN" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'LendMN апп дээрх ирсэн нэхэмжлэлээ шалгана',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}
